import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const ADD_BUTTON_SELECTOR = '[data-search-add-to-cart]';
const ADDING_TEXT = 'Adding...';
const ADDED_TEXT = 'Added';
const SUCCESS_RESET_DELAY = 1000;
const ERROR_RESET_DELAY = 1200;

/**
 * Returns section ids for cart components that need to be hydrated after AJAX add.
 *
 * @returns {string}
 */
function getCartSectionIds() {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.add(item.dataset.sectionId);
    }
  });

  return Array.from(sectionIds).join(',');
}

/**
 * @returns {Promise<any>}
 */
async function refreshCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    ...fetchConfig('json'),
  });

  if (!response.ok) {
    throw new Error('Could not refresh cart');
  }

  return response.json();
}

/**
 * @returns {boolean}
 */
function isSearchModalOpen() {
  return Boolean(document.querySelector('#search-modal dialog[open], .search-modal__content[open]'));
}

/**
 * Moves the cart drawer above the search modal browser top layer.
 *
 * @param {Element} cartDrawer
 */
function promoteCartDrawerAboveSearch(cartDrawer) {
  if (!isSearchModalOpen()) return;

  const panel = cartDrawer.querySelector('dialog.theme-drawer__dialog');

  if (!(panel instanceof HTMLDialogElement)) return;

  try {
    if (panel.open) {
      panel.close();
    }

    panel.showModal();
    panel.querySelector('.theme-drawer__close-button')?.focus();
  } catch (_error) {
    panel.style.zIndex = '2147483647';
  }
}

/**
 * Opens the cart drawer as a fallback if the theme event listener did not.
 */
function openCartDrawer() {
  const cartDrawer = document.querySelector('theme-drawer#cart-drawer');

  if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

  cartDrawer.open();
  window.requestAnimationFrame(() => promoteCartDrawerAboveSearch(cartDrawer));
}

/**
 * @param {HTMLButtonElement} button
 * @param {string} text
 */
function setButtonText(button, text) {
  button.textContent = text;
}

document.addEventListener('click', async (event) => {
  const target = event.target;
  const button = target instanceof Element ? target.closest(ADD_BUTTON_SELECTOR) : null;

  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  event.preventDefault();
  event.stopPropagation();

  const originalText = button.textContent?.trim() || '';
  const quantity = 1;
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  const formData = new FormData();
  const sectionIds = getCartSectionIds();

  formData.set('id', variantId);
  formData.set('quantity', quantity.toString());

  if (sectionIds) {
    formData.set('sections', sectionIds);
  }

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  setButtonText(button, ADDING_TEXT);

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'search',
      lines: [
        {
          merchandiseId: variantId,
          quantity,
        },
      ],
      promise: deferredEventPromise.promise,
    })
  );

  try {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    });
    const result = await response.json();

    if (result.status) {
      throw new Error(result.message || result.description || 'Add to cart failed');
    }

    const cart = await refreshCart();

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'search-product-card',
        itemCount: quantity,
        sections: result.sections,
        didError: false,
      },
    });

    setButtonText(button, ADDED_TEXT);

    // Keep search open, but promote the cart drawer above the search modal.
    window.requestAnimationFrame(openCartDrawer);

    window.setTimeout(() => {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      setButtonText(button, originalText);
    }, SUCCESS_RESET_DELAY);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Add to cart failed';

    button.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'INVALID',
      })
    );

    refreshCart()
      .then((cart) => {
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'search-product-card',
            itemCount: quantity,
            didError: true,
          },
        });
      })
      .catch(deferredEventPromise.reject);

    setButtonText(button, message);

    window.setTimeout(() => {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      setButtonText(button, originalText);
    }, ERROR_RESET_DELAY);
  }
});
